import { AppModal } from '@euler/app/components/modal/AppModal';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { useVoiceRecognize } from '@euler/app/components/voice/useVoiceRecognize';
import { Spinner } from '@euler/components/Spinner';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { isAutomaticKeyboardEnabled } from '@euler/lib/keyboard';
import { VoiceRecognizeOptions } from '@euler/services/voice.service';
import { delayed } from '@euler/utils';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
} from '@euler/utils/hooks';
import { Mic } from 'lucide-react-native';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  NativeSyntheticEvent,
  Platform,
  ReturnKeyTypeOptions,
  StyleProp,
  StyleSheet,
  TextInputSubmitEditingEventData,
  TextStyle,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import KeyboardManager from 'react-native-keyboard-manager';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';
import {
  AutoResizeTextInput,
  AutoResizeTextInputRef,
} from './AutoResizeTextInput';

const kDefaultPadding = 16;
const kDefaultLineHeight = 24;
const kDefaultFontSize = 17;
const kDefaultMinNumberOfLines = 5;
const kDefaultMaxNumberOfLines = 10;

export type FloatingTextInputController = {
  value?: string;
  placeholder?: string;
  lineHeight?: number;
  minNumberOfLines?: number;
  maxNumberOfLines?: number;
  returnKeyType?: ReturnKeyTypeOptions;
  returnKeyLabel?: string;
  noToolbarPlaceholder?: boolean;
  style?: StyleProp<TextStyle>;
  /**
   * Render the voice-input mic button. Defaults to `false`. When the
   * caller opts in but the store has voice input disabled, the mic is
   * silently suppressed by `useShowFloatingTextInput`.
   */
  withVoiceInput?: boolean;
  /**
   * Optional voice-recognition tuning. Forwarded to the recognize API when
   * the user records via the mic button. Each flag defaults to `true` on the
   * server when omitted. Only applies when `withVoiceInput` is true.
   */
  voiceInput?: VoiceRecognizeOptions;
  onDone?: (text: string) => void;
};

const floatingTextInputController$ = new BehaviorSubject<
  FloatingTextInputController | undefined
>(undefined);

export const useShowFloatingTextInput = () => {
  const setController = useBehaviorSubjectUpdater(floatingTextInputController$);
  const { isVoiceInputEnabled } = useVoiceFeatures();
  return useCallback(
    (controller: FloatingTextInputController) => {
      // The caller expresses intent via `withVoiceInput`; the hook AND's it
      // with the store's voice-input feature flag so callers don't have to
      // do the feature check themselves.
      setController({
        ...controller,
        withVoiceInput:
          controller.withVoiceInput !== false && isVoiceInputEnabled,
      });
    },
    [setController, isVoiceInputEnabled],
  );
};

export const FloatingTextInput = memo(() => {
  const isMobile = useIsMobileLayout();

  const [controller, setController] = useBehaviorSubject(
    floatingTextInputController$,
  );

  const onClose = useCallback(() => {
    setController(undefined);
  }, [setController]);

  if (!controller) return null;

  if (!isMobile || Platform.OS === 'web') {
    return (
      <AppModal visible={true} onDismiss={onClose}>
        <Content controller={controller} onClose={onClose} showButton />
      </AppModal>
    );
  }

  return (
    <TouchableWithoutFeedback onPress={onClose}>
      <KeyboardAvoidingView
        behavior="padding"
        style={StyleSheet.absoluteFill}
        css={`
          justify-content: flex-end;
          background-color: rgba(0, 0, 0, 0.09);
        `}
      >
        <Content controller={controller} onClose={onClose} />
      </KeyboardAvoidingView>
    </TouchableWithoutFeedback>
  );
});

const Content = memo(
  ({
    controller,
    onClose,
    showButton,
  }: {
    controller: FloatingTextInputController;
    onClose: () => void;
    showButton?: boolean;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const [text, setText] = useState(controller.value ?? '');
    const voice = useVoiceRecognize(controller.voiceInput);
    const { isRecording, isRecognizing } = voice;
    const isRecordingRef = useRef(false);
    isRecordingRef.current = isRecording || isRecognizing;
    const inputRef = useRef<AutoResizeTextInputRef>(null);

    // Track caret position so recognized text is inserted at the cursor
    // rather than always appended. `pendingSelection` overrides the caret
    // for one render after a programmatic insertion, then clears.
    const selectionRef = useRef<{ start: number; end: number }>({
      start: 0,
      end: 0,
    });
    const [pendingSelection, setPendingSelection] = useState<
      { start: number; end: number } | undefined
    >(undefined);
    useEffect(() => {
      if (pendingSelection) setPendingSelection(undefined);
    }, [pendingSelection]);
    const onSelectionChange = useCallback(
      (
        e: NativeSyntheticEvent<{ selection: { start: number; end: number } }>,
      ) => {
        selectionRef.current = e.nativeEvent.selection;
      },
      [],
    );

    const onMicPress = useCallback(() => {
      voice.start({
        onResult: recognized => {
          // The recording overlay has already closed by the time we get
          // here; refocus the input so the keyboard slides back up.
          inputRef.current?.focus();
          setText(prev => {
            const sel = selectionRef.current;
            const start = Math.min(Math.max(sel.start, 0), prev.length);
            const end = Math.min(Math.max(sel.end, start), prev.length);
            const next = prev.slice(0, start) + recognized + prev.slice(end);
            const caret = start + recognized.length;
            setPendingSelection({ start: caret, end: caret });
            selectionRef.current = { start: caret, end: caret };
            return next;
          });
        },
      });
    }, [voice]);

    const onSubmitEditing = useCallback(
      (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        // While recording, the keyboard's toolbar 确定 button (or any blur)
        // should just dismiss the keyboard — don't commit / close the
        // floating input out from under the recording overlay.
        if (isRecordingRef.current) return;
        controller.onDone?.(e.nativeEvent.text);
        onClose();
      },
      [controller, onClose],
    );

    const onButtonPress = useCallback(() => {
      controller.onDone?.(text);
      onClose();
    }, [controller, onClose, text]);

    const shouldEnableAutomaticKeyboard = useRef(false);

    useEffect(() => {
      let clear: (() => void) | undefined = undefined;
      if (controller) {
        if (Platform.OS === 'ios' && isAutomaticKeyboardEnabled) {
          KeyboardManager.setEnable(false);
          shouldEnableAutomaticKeyboard.current = true;
          if (controller.noToolbarPlaceholder) {
            KeyboardManager.setShouldShowToolbarPlaceholder(false);
          }
        }
        clear = delayed(() => {
          setText(controller.value ?? '');
        }, 50);
      }
      return () => {
        clear?.();
        if (shouldEnableAutomaticKeyboard.current) {
          KeyboardManager.setEnable(true);
          shouldEnableAutomaticKeyboard.current = false;
          if (controller.noToolbarPlaceholder) {
            KeyboardManager.setShouldShowToolbarPlaceholder(true);
          }
        }
        setText('');
      };
    }, [controller]);

    const defaultPadding = rpx(kDefaultPadding);
    const defaultLineHeight = rpx(kDefaultLineHeight);
    const defaultFontSize = rpx(kDefaultFontSize);
    const isSingleLine = controller.maxNumberOfLines === 1;

    return (
      <View
        css={`
          background-color: #fff;
          box-shadow: 0 0 ${rpx(8)}px rgba(0, 0, 0, 0.15);
          padding-top: ${defaultPadding}px;
          padding-bottom: ${showButton ? 0 : defaultPadding}px;
        `}
      >
        <View>
          <AutoResizeTextInput
            ref={inputRef}
            multiline={
              controller.maxNumberOfLines == null ||
              controller.maxNumberOfLines > 1
            }
            autoFocus
            value={text}
            onChangeText={setText}
            onSelectionChange={onSelectionChange}
            selection={pendingSelection}
            onEndEditing={onSubmitEditing}
            placeholder={controller.placeholder}
            textAlignVertical="top"
            lineHeight={controller.lineHeight ?? defaultLineHeight}
            minNumberOfLines={
              controller.minNumberOfLines ?? kDefaultMinNumberOfLines
            }
            maxNumberOfLines={
              controller.maxNumberOfLines ?? kDefaultMaxNumberOfLines
            }
            returnKeyType={controller.returnKeyType}
            returnKeyLabel={controller.returnKeyLabel}
            style={[
              {
                fontFamily: FontFamily.NotoSans.Regular,
                fontSize: defaultFontSize,
                lineHeight: controller.lineHeight ?? defaultLineHeight,
                paddingLeft: defaultPadding,
                paddingRight: defaultPadding,
              },
              controller.style,
            ]}
          />
          {controller.withVoiceInput && (isRecognizing || !isRecording) && (
            <View
              pointerEvents="box-none"
              // For single-line inputs the wrapper is roughly one line tall,
              // so anchoring to the bottom pushes the button outside the
              // input. Stretch full-height + center vertically instead;
              // multi-line inputs keep bottom-anchored placement.
              style={
                isSingleLine
                  ? {
                      position: 'absolute',
                      right: rpx(8),
                      top: 0,
                      bottom: 0,
                      justifyContent: 'center',
                    }
                  : {
                      position: 'absolute',
                      right: rpx(8),
                      bottom: rpx(8),
                    }
              }
            >
              {isRecognizing ? (
                <View
                  style={{
                    width: rpx(36),
                    height: rpx(36),
                    borderRadius: rpx(18),
                    backgroundColor: theme.link,
                    alignItems: 'center',
                    justifyContent: 'center',
                    shadowColor: '#000',
                    shadowOpacity: 0.18,
                    shadowRadius: rpx(4),
                    shadowOffset: { width: 0, height: rpx(2) },
                    elevation: 3,
                  }}
                >
                  <Spinner size={rpx(18)} color="#fff" />
                </View>
              ) : (
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={onMicPress}
                  hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  style={{
                    width: rpx(36),
                    height: rpx(36),
                    borderRadius: rpx(18),
                    backgroundColor: theme.link,
                    alignItems: 'center',
                    justifyContent: 'center',
                    shadowColor: '#000',
                    shadowOpacity: 0.18,
                    shadowRadius: rpx(4),
                    shadowOffset: { width: 0, height: rpx(2) },
                    elevation: 3,
                  }}
                >
                  <Mic color="#fff" size={rpx(18)} strokeWidth={2.4} />
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
        {showButton && (
          <TouchableOpacity
            css={`
              height: ${rpx(44)}px;
              background-color: ${theme.link};
              align-items: center;
              justify-content: center;
              margin-top: ${defaultPadding}px;
            `}
            activeOpacity={0.95}
            onPress={onButtonPress}
          >
            <Typography.Label regular color="#fff" size={rpx(16)}>
              确定
            </Typography.Label>
          </TouchableOpacity>
        )}
      </View>
    );
  },
);
